import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Active types shown in the dropdown
const TYPES = ['Sick', 'Casual', 'Paid', 'Unpaid', 'Maternity/Paternity', 'Bereavement'];

// Chip styling per type (includes legacy fallbacks so old records still render)
const TYPE_LABELS = {
  Sick: { l: 'Sick Leave', bg: '#fee2e2', fg: '#991b1b', icon: '🤒' },
  Casual: { l: 'Casual Leave', bg: '#dbeafe', fg: '#1e40af', icon: '🌴' },
  Paid: { l: 'Paid Leave', bg: '#dcfce7', fg: '#166534', icon: '🏖️' },
  Unpaid: { l: 'Unpaid Leave', bg: '#f3f4f6', fg: '#374151', icon: '⊘' },
  'Maternity/Paternity': { l: 'Maternity/Paternity', bg: '#fce7f3', fg: '#9d174d', icon: '👶' },
  Bereavement: { l: 'Bereavement', bg: '#e5e7eb', fg: '#1f2937', icon: '🕊️' },
  Earned: { l: 'Earned', bg: '#dcfce7', fg: '#166534', icon: '🏖️' },
  'Comp Off': { l: 'Comp Off', bg: '#fef3c7', fg: '#92400e', icon: '🔄' },
  Maternity: { l: 'Maternity', bg: '#fce7f3', fg: '#9d174d', icon: '👶' },
  Paternity: { l: 'Paternity', bg: '#e0e7ff', fg: '#3730a3', icon: '👨' },
};
const TypeChip = ({ t }) => {
  const c = TYPE_LABELS[t] || { l: t || '—', bg: '#f3f4f6', fg: '#374151', icon: '•' };
  return (
    <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: c.bg, color: c.fg }}>
      <span className="mr-1">{c.icon}</span>{c.l}
    </span>
  );
};

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

export default function MyLeavesPage() {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [profile, setProfile] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ leave_type: 'Sick', start_date: '', end_date: '', reason: '' });

  const load = async () => {
    const [me, lv] = await Promise.all([
      axios.get(`${API_URL}/api/my/profile`, { withCredentials: true }),
      axios.get(`${API_URL}/api/my/leaves`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setProfile(me.data);
    setItems(lv.data || []);
    if (me.data?.employee_id) {
      try {
        const b = await axios.get(`${API_URL}/api/leaves/balance/${me.data.employee_id}`, { withCredentials: true });
        setBalance(b.data);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error('Pick start and end dates');
    if (!profile?.employee_id) return toast.error('Your login is not linked to an employee record. Ask admin to link it on the Users page.');
    try {
      await axios.post(`${API_URL}/api/my/leaves`, form, { withCredentials: true });
      toast.success('Leave request submitted');
      setShow(false);
      setForm({ leave_type: 'Sick', start_date: '', end_date: '', reason: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const cancel = async (l) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await axios.delete(`${API_URL}/api/my/leaves/${l.id}`, { withCredentials: true });
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4" data-testid="my-leaves-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold" style={{ color: '#1a2e4a' }}>My Leaves</h2>
        <Button onClick={() => setShow(true)} data-testid="new-leave-btn">Request Leave</Button>
      </div>

      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Eligible (post-probation)</div><div className="text-lg font-bold mt-0.5">{balance.eligible ? 'Yes' : 'No'}</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Earned</div><div className="text-lg font-bold text-blue-700 mt-0.5">{Number(balance.earned_days || 0).toFixed(1)}d</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Used</div><div className="text-lg font-bold text-rose-700 mt-0.5">{Number(balance.used_days || 0).toFixed(1)}d</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Balance</div><div className="text-lg font-bold text-emerald-700 mt-0.5">{Number(balance.balance || 0).toFixed(1)}d</div></Card>
        </div>
      )}

      <Card className="p-3">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-gray-500 border-b">
            <tr>
              <th className="text-left py-1.5">Type</th>
              <th className="text-left">From</th>
              <th className="text-left">To</th>
              <th className="text-left">Days</th>
              <th className="text-left">Status</th>
              <th className="text-left">Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="py-3 text-center text-gray-400 italic">No leave requests yet.</td></tr>}
            {items.map((l) => (
              <tr key={l.id} className="border-b last:border-0" data-testid={`leave-row-${l.id}`}>
                <td className="py-1.5"><TypeChip t={l.leave_type} /></td>
                <td>{l.from_date}</td>
                <td>{l.to_date}</td>
                <td>{Number(l.days || 0).toFixed(1)}</td>
                <td><span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLOR[l.status] || 'bg-gray-100 text-gray-700'}`}>{(l.status || '').toUpperCase()}</span></td>
                <td className="text-gray-600 max-w-xs truncate">{l.reason}</td>
                <td>{l.status === 'pending' && <button onClick={() => cancel(l)} className="text-rose-600 hover:underline text-[11px]" data-testid={`cancel-leave-${l.id}`}>cancel</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShow(false)}>
          <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3" style={{ color: '#1a2e4a' }}>Request Leave</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Type</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="leave-type-select">
                  {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t].icon} {TYPE_LABELS[t].l}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Start *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
              <div><Label className="text-xs">End *</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
              <div className="col-span-2"><Label className="text-xs">Reason</Label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[70px]" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-leave-btn">Submit Request</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
